import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  Grid,
  Stack,
  Button,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';
import moment from 'moment';
import Page from 'src/components/Page';
import CreatedBy from 'src/components/CreatedBy';
import CustomPagination from 'src/components/CustomPagination';
import SearchNotFound from 'src/components/SearchNotFound';
import { TransactionModal } from 'src/components/transaction-modal';
import { useHeaderBreadcrumbs } from 'src/context/header/HeaderContext';
import { useAuth } from 'src/context/auth/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'src/hooks/useDebounce';
import { transactionService } from 'src/services/transaction.service';
import { TransactionTypes } from './TransactionTypes';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  isLoading?: boolean;
}

function MetricCard({ title, value, subtitle, icon, iconBg, iconColor, isLoading }: MetricCardProps) {
  return (
    <Card
      sx={{
        p: 1.75,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.15s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        },
      }}
    >
      <Box>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}
        >
          {title}
        </Typography>
        {isLoading ? (
          <Skeleton variant="text" width={50} height={28} />
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', my: 0.25, fontSize: '1.25rem', letterSpacing: '-0.01em' }}>
            {value}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
          {subtitle}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.75,
          bgcolor: iconBg,
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon icon={icon} width={20} height={20} />
      </Box>
    </Card>
  );
}

function getStatusBadge(status?: string | null) {
  switch (status) {
    case 'Approved':
      return {
        label: 'Approved',
        color: '#059669',
        bgcolor: 'rgba(16, 185, 129, 0.12)',
        icon: 'solar:check-circle-bold-duotone',
      };
    case 'Pending':
      return {
        label: 'Pending',
        color: '#D97706',
        bgcolor: 'rgba(245, 158, 11, 0.12)',
        icon: 'solar:hourglass-bold-duotone',
      };
    case 'Rejected':
    case 'Declined':
      return {
        label: 'Rejected',
        color: '#DC2626',
        bgcolor: 'rgba(239, 68, 68, 0.12)',
        icon: 'solar:close-circle-bold-duotone',
      };
    default:
      return {
        label: status || 'Completed',
        color: '#059669',
        bgcolor: 'rgba(16, 185, 129, 0.12)',
        icon: 'solar:check-circle-bold-duotone',
      };
  }
}

export default function ManageTransactionTypes(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { getBaseRoute, user, hasPermission } = useAuth();

  const [currentTab, setCurrentTab] = useState<number>((location.state as { tab?: number })?.tab ?? 0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [searchInput, setSearchInput] = useState('');
  const [typeTab, setTypeTab] = useState<'ALL' | 'credit' | 'debit' | 'pending'>('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const debouncedSearch = useDebounce(searchInput, 300);

  // Automatic Auth Scoping: Admins see all; teachers see their assigned scholars automatically
  const canViewAll = hasPermission('transactions.view_all');
  const teacherUserId = !canViewAll ? (user?.id || user?.email) : undefined;
  const canManageTypes = hasPermission('transaction_types.manage');

  useHeaderBreadcrumbs({
    breadcrumbs: [
      { label: 'S.T.R.E.E.T Cents', path: getBaseRoute('/street-cents') },
      { label: currentTab === 0 ? 'Scholar Ledger Records' : 'Transaction Types Catalog' },
    ],
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Query metrics (scoped automatically)
  const { data: metrics, isLoading: isMetricsLoading } = useQuery({
    queryKey: ['streetCentsMetrics', { teacherUserId }],
    queryFn: () =>
      transactionService.getStreetCentsMetrics({
        teacherUserId,
      }),
    staleTime: 30000,
  });

  // Query transactions list (scoped automatically)
  const { data: transactionsResponse, isLoading: isLoadingList } = useQuery({
    queryKey: ['streetCentsTransactions', page, rowsPerPage, debouncedSearch, typeTab, teacherUserId],
    queryFn: () =>
      transactionService.getTransactions(page, rowsPerPage, {
        teacherUserId,
        search: debouncedSearch,
        type: typeTab === 'credit' ? 'credit' : typeTab === 'debit' ? 'debit' : undefined,
        status: typeTab === 'pending' ? 'Pending' : undefined,
      }),
    staleTime: 10000,
    enabled: currentTab === 0,
  });

  const transactionList = transactionsResponse?.data || [];
  const totalCount = transactionsResponse?.total || 0;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  return (
    <Page title="S.T.R.E.E.T Cents Hub | Gold Minds Portal">
      <Container maxWidth={false} sx={{ px: { xs: 1, sm: 1.5, md: 2 }, pt: 0.5, pb: 4 }}>
        {/* Header Ribbon */}
        <Box sx={{ mb: 2, mt: 0.25 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: 'rgba(37, 99, 235, 0.12)',
                    color: '#2563EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon icon="solar:wallet-money-bold-duotone" width={22} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: -0.3, fontSize: '1.35rem' }}>
                    S.T.R.E.E.T Cents
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    Track scholar earnings, classroom debit deductions, and payroll balances in real-time.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Stack direction="row" spacing={1}>
              {canManageTypes && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Icon icon="solar:library-bold" width={16} />}
                  onClick={() => navigate(getBaseRoute('/add-transaction-type'))}
                  sx={{
                    height: 36,
                    borderRadius: 1.5,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    textTransform: 'none',
                    borderColor: 'divider',
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'grey.100', borderColor: 'text.secondary' },
                  }}
                >
                  Catalog Library
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<Icon icon="solar:add-circle-bold" width={18} />}
                onClick={() => setIsModalOpen(true)}
                sx={{
                  height: 36,
                  px: 2,
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  borderRadius: 1.5,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
                }}
              >
                New Transaction
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* 5 Top KPI Analytics Cards */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Total Ledger"
              value={metrics?.totalTransactions?.toLocaleString() ?? 0}
              subtitle="Logged records"
              icon="solar:card-2-bold-duotone"
              iconBg="rgba(37, 99, 235, 0.12)"
              iconColor="#2563EB"
              isLoading={isMetricsLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Total Credits (+)"
              value={`$${(metrics?.totalCredits ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              subtitle="Earnings & paychecks"
              icon="solar:card-recive-bold-duotone"
              iconBg="rgba(16, 185, 129, 0.12)"
              iconColor="#059669"
              isLoading={isMetricsLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Total Debits (-)"
              value={`$${(metrics?.totalDebits ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              subtitle="Expenses & fines"
              icon="solar:card-send-bold-duotone"
              iconBg="rgba(239, 68, 68, 0.12)"
              iconColor="#DC2626"
              isLoading={isMetricsLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Net Volume"
              value={`${(metrics?.netVolume ?? 0) >= 0 ? '+$' : '-$'}${Math.abs(metrics?.netVolume ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              subtitle="Net circulation"
              icon="solar:chart-2-bold-duotone"
              iconBg="rgba(124, 58, 237, 0.12)"
              iconColor="#7C3AED"
              isLoading={isMetricsLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <MetricCard
              title="Pending Approvals"
              value={metrics?.pendingCount?.toLocaleString() ?? 0}
              subtitle="Awaiting sign-off"
              icon="solar:hourglass-bold-duotone"
              iconBg="rgba(245, 158, 11, 0.12)"
              iconColor="#D97706"
              isLoading={isMetricsLoading}
            />
          </Grid>
        </Grid>

        {/* Navigation Tabs */}
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                minHeight: 44,
                py: 1,
                px: 2,
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 700,
                },
              },
            }}
          >
            <Tab
              icon={<Icon icon="solar:clipboard-list-bold-duotone" width={18} />}
              iconPosition="start"
              label={`Scholar Ledger Records (${metrics?.totalTransactions ?? 0})`}
            />
            <Tab
              icon={<Icon icon="solar:library-bold-duotone" width={18} />}
              iconPosition="start"
              label="Transaction Types Catalog"
            />
          </Tabs>
        </Box>

        {/* Tab 0: Scholar Transaction Ledger */}
        {currentTab === 0 && (
          <Card
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 'none',
              overflow: 'hidden',
            }}
          >
            {/* Controls Bar: Search & Filter Tabs */}
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
                bgcolor: 'background.neutral',
              }}
            >
              {/* Search Input */}
              <TextField
                size="small"
                placeholder="Search scholar name, email, or details..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="solar:magnifer-linear" width={18} style={{ color: '#94A3B8' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    height: 36,
                    bgcolor: 'background.paper',
                    borderRadius: 1.5,
                    fontSize: '0.8125rem',
                    maxWidth: { xs: '100%', md: 320 },
                  },
                }}
              />

              {/* Capsule Filter Chips */}
              <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', py: 0.25 }}>
                <Chip
                  label="All Transactions"
                  size="small"
                  onClick={() => {
                    setTypeTab('ALL');
                    setPage(1);
                  }}
                  sx={{
                    height: 32,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    bgcolor: typeTab === 'ALL' ? 'primary.main' : 'background.paper',
                    color: typeTab === 'ALL' ? '#FFFFFF' : 'text.primary',
                    border: '1px solid',
                    borderColor: typeTab === 'ALL' ? 'primary.main' : 'divider',
                    '&:hover': { bgcolor: typeTab === 'ALL' ? 'primary.dark' : 'grey.100' },
                  }}
                />
                <Chip
                  icon={<Icon icon="solar:arrow-down-left-bold" width={14} />}
                  label="Credits (+)"
                  size="small"
                  onClick={() => {
                    setTypeTab('credit');
                    setPage(1);
                  }}
                  sx={{
                    height: 32,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    bgcolor: typeTab === 'credit' ? '#059669' : 'background.paper',
                    color: typeTab === 'credit' ? '#FFFFFF' : '#059669',
                    border: '1px solid',
                    borderColor: typeTab === 'credit' ? '#059669' : 'divider',
                    '&:hover': { bgcolor: typeTab === 'credit' ? '#047857' : 'rgba(16, 185, 129, 0.08)' },
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
                <Chip
                  icon={<Icon icon="solar:arrow-up-right-bold" width={14} />}
                  label="Debits (-)"
                  size="small"
                  onClick={() => {
                    setTypeTab('debit');
                    setPage(1);
                  }}
                  sx={{
                    height: 32,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    bgcolor: typeTab === 'debit' ? '#DC2626' : 'background.paper',
                    color: typeTab === 'debit' ? '#FFFFFF' : '#DC2626',
                    border: '1px solid',
                    borderColor: typeTab === 'debit' ? '#DC2626' : 'divider',
                    '&:hover': { bgcolor: typeTab === 'debit' ? '#B91C1C' : 'rgba(220, 38, 38, 0.08)' },
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
                <Chip
                  icon={<Icon icon="solar:hourglass-bold-duotone" width={15} />}
                  label="Pending"
                  size="small"
                  onClick={() => {
                    setTypeTab('pending');
                    setPage(1);
                  }}
                  sx={{
                    height: 32,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    bgcolor: typeTab === 'pending' ? '#D97706' : 'background.paper',
                    color: typeTab === 'pending' ? '#FFFFFF' : '#D97706',
                    border: '1px solid',
                    borderColor: typeTab === 'pending' ? '#D97706' : 'divider',
                    '&:hover': { bgcolor: typeTab === 'pending' ? '#B45309' : 'rgba(245, 158, 11, 0.08)' },
                  }}
                />
              </Stack>
            </Box>

            {/* Table */}
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Scholar
                    </TableCell>
                    <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Category & Details
                    </TableCell>
                    <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Amount
                    </TableCell>
                    <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Balance After
                    </TableCell>
                    <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Created By
                    </TableCell>
                    <TableCell sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Timestamp
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.75, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoadingList ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={32} thickness={4} />
                        <Typography variant="caption" sx={{ mt: 1.25, color: 'text.secondary', fontWeight: 500, display: 'block', fontSize: '0.8125rem' }}>
                          Loading transaction ledger...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : transactionList.length > 0 ? (
                    transactionList.map((row: any) => {
                      const studentName = `${row?.student?.user?.firstname || ''} ${row?.student?.user?.lastname || ''}`.trim() || 'Unnamed Scholar';
                      const scholarId = row?.studentid || row?.student?.id;
                      const viewStudentPath = scholarId ? getBaseRoute(`/view-student/${scholarId}`) : null;
                      const isCredit = row?.transaction_type?.type === 'credit';
                      const amount = Number(row?.amount || 0);
                      const balanceAfter = Number(row?.balanceafter ?? row?.student?.balance ?? 0);
                      const statusMeta = getStatusBadge(row?.status);
                      const viewTransactionPath = getBaseRoute(`/street-cents/${row.id}`);

                      return (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease',
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                          onClick={() => navigate(viewTransactionPath)}
                        >
                          {/* Scholar Info */}
                          <TableCell sx={{ py: 2 }}>
                            <Tooltip title={viewStudentPath ? 'View Scholar Profile' : ''}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                                onClick={(e) => {
                                  if (viewStudentPath) {
                                    e.stopPropagation();
                                    navigate(viewStudentPath);
                                  }
                                }}
                                sx={{
                                  cursor: viewStudentPath ? 'pointer' : 'default',
                                  width: 'fit-content',
                                  '&:hover .scholar-name': {
                                    color: viewStudentPath ? 'primary.main' : 'text.primary',
                                    textDecoration: viewStudentPath ? 'underline' : 'none',
                                  },
                                  '&:hover .scholar-avatar': {
                                    transform: viewStudentPath ? 'scale(1.06)' : 'none',
                                    boxShadow: viewStudentPath ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
                                  },
                                }}
                              >
                                <Avatar
                                  className="scholar-avatar"
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    bgcolor: 'primary.light',
                                    color: 'primary.dark',
                                    fontWeight: 700,
                                    fontSize: '0.8125rem',
                                    transition: 'all 0.15s ease',
                                  }}
                                >
                                  {studentName.charAt(0)}
                                </Avatar>
                                <Box>
                                  <Typography
                                    className="scholar-name"
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      color: 'text.primary',
                                      fontSize: '0.875rem',
                                      lineHeight: 1.25,
                                      transition: 'color 0.15s ease',
                                    }}
                                  >
                                    {studentName}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 400 }}>
                                    {row?.student?.user?.email || 'No email'}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Tooltip>
                          </TableCell>

                          {/* Category & Details */}
                          <TableCell sx={{ py: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                              <Box
                                sx={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 1.5,
                                  bgcolor: isCredit ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                                  color: isCredit ? '#059669' : '#DC2626',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <Icon
                                  icon={isCredit ? 'solar:card-recive-bold-duotone' : 'solar:card-send-bold-duotone'}
                                  width={19}
                                />
                              </Box>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem', lineHeight: 1.25 }}>
                                  {row?.transaction_type?.name || row?.description || 'General Ledger Entry'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'text.secondary',
                                    fontSize: '0.75rem',
                                    fontWeight: 400,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    maxWidth: 280,
                                  }}
                                >
                                  {row?.description || row?.reason || (isCredit ? 'Credit deposit' : 'Debit deduction')}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          {/* Amount */}
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              icon={
                                <Icon
                                  icon={isCredit ? 'solar:card-recive-bold-duotone' : 'solar:card-send-bold-duotone'}
                                  width={13}
                                  color={isCredit ? '#059669' : '#DC2626'}
                                />
                              }
                              label={`${isCredit ? '+' : '-'}$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                              size="small"
                              sx={{
                                height: 25,
                                fontSize: '0.8125rem',
                                fontWeight: 700,
                                borderRadius: 1.25,
                                bgcolor: isCredit ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                color: isCredit ? '#059669' : '#DC2626',
                                border: '1px solid',
                                borderColor: isCredit ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                              }}
                            />
                          </TableCell>

                          {/* Balance After */}
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}>
                              ${balanceAfter.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                          </TableCell>

                          {/* Status */}
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              icon={<Icon icon={statusMeta.icon} width={13} />}
                              label={statusMeta.label}
                              size="small"
                              sx={{
                                height: 25,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                color: statusMeta.color,
                                bgcolor: statusMeta.bgcolor,
                                border: `1px solid ${statusMeta.color}22`,
                                '& .MuiChip-icon': {
                                  color: `${statusMeta.color} !important`,
                                  marginLeft: '4px',
                                },
                              }}
                            />
                          </TableCell>

                          {/* Created By */}
                          <TableCell sx={{ py: 2 }}>
                            <CreatedBy creator={row?.creator} />
                          </TableCell>

                          {/* Timestamp */}
                          <TableCell sx={{ py: 2 }}>
                            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.8125rem' }}>
                              {row?.createdat ? moment(row.createdat).format('MMM D, YYYY') : '--'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 400 }}>
                              {row?.createdat ? moment(row.createdat).format('h:mm A') : ''}
                            </Typography>
                          </TableCell>

                          {/* Actions */}
                          <TableCell align="right" sx={{ py: 2 }}>
                            <Tooltip title="View Transaction Details">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(viewTransactionPath);
                                }}
                                sx={{
                                  p: 0.75,
                                  bgcolor: 'grey.100',
                                  color: 'text.secondary',
                                  '&:hover': {
                                    bgcolor: 'primary.light',
                                    color: 'primary.dark',
                                  },
                                }}
                              >
                                <Icon icon="solar:eye-bold-duotone" width={18} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 4 }}>
                        <SearchNotFound searchQuery={debouncedSearch} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <CustomPagination
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[8, 10, 25]}
              />
            </Box>
          </Card>
        )}

        {/* Tab 1: Transaction Types Catalog */}
        {currentTab === 1 && <TransactionTypes />}

        {/* New Transaction Modal */}
        <TransactionModal
          open={isModalOpen}
          closeModal={() => setIsModalOpen(false)}
        />
      </Container>
    </Page>
  );
}
